import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useDeleteJobCardMutation,
  useGetJobCardByReferenceQuery,
  useAddChangeLogMutation,
} from "../api/apiSlice";
import { showError, showSuccess } from "../../Global/notifications/notificationsSlice";

// Abort the job on this page, then return to Home. Shared by every Abort control (the
// step footer, the connection-test interlude) so they all behave the same.
//
// Aborting a redo resets it rather than closing it (D2): the job goes back to Alerts. The
// redo's start was logged ("Installer is redoing declined install"), so the reset is
// logged too, or the device's activity history ends on a redo that is no longer running.
export default function useAbortJob(job_card_reference_id) {
  const abortReasonRef = useRef("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteJobCard, { isSuccess, isLoading, data }] = useDeleteJobCardMutation();
  const [addChangeLog] = useAddChangeLogMutation();
  // A redo keeps QE's Declined verdict until it is resubmitted.
  const { data: jobCard } = useGetJobCardByReferenceQuery(job_card_reference_id, {
    skip: !job_card_reference_id,
  });
  const isRedo = jobCard?.qe_job_state === "Declined";

  useEffect(() => {
    if (!isSuccess) return;
    if (data?.redo_reset) {
      const serial_number = jobCard?.install_state === "UN" ? jobCard?.archived_serial_number : jobCard?.serial_number;
      if (serial_number) {
        addChangeLog({ body: { serial_number, change: "Installer stopped the redo; it is back in Alerts to start again" } });
      }
      dispatch(showSuccess("The redo was reset. It's back in Alerts to start again."));
    } else if (["UN", "RE"].includes(jobCard?.install_state) && jobCard?.archived_serial_number) {
      // The start was logged on the old device, so the abort is too: D16 read as two
      // uninstalls started and one finished, with nothing between (stress test 26/10, P4).
      const what = jobCard.install_state === "UN" ? "Uninstall" : "Replacement";
      const reason = abortReasonRef.current;
      addChangeLog({ body: { serial_number: jobCard.archived_serial_number, change: `${what} aborted${reason ? `: ${reason}` : ""}` } });
    }
    navigate("/installer/");
  }, [isSuccess]);

  // An abort needs the server. Offline it used to go nowhere with no word to the installer
  // (Confirm "did nothing" until signal came back), and a refused abort was silent too (N9).
  // The buttons re-enable on failure, so the installer can try again.
  // reason goes on the tracker's aborted list (D10); a stopped redo needs none.
  const abort = (reason = "") => {
    if (!navigator.onLine) {
      dispatch(showError("You need signal to abort this job. Try again once you're reconnected."));
      return;
    }
    abortReasonRef.current = reason;
    deleteJobCard({ body: { reference_id: job_card_reference_id, reason } })
      .unwrap()
      .catch((error) => {
        // Refused because the job was already finished or aborted elsewhere (a stale
        // tab): nothing is left to do on this page, so go Home rather than leave the
        // dialog open on a dead job (N11).
        if (error?.status === 409) {
          dispatch(showError("This job was already finished or aborted on another screen."));
          navigate("/installer/");
          return;
        }
        dispatch(showError(
          error?.data?.error || error?.data?.error_message
            || "Couldn't abort the job. Check your signal and try again."
        ));
      });
  };

  return { abort, isLoading, isSuccess, isRedo, installState: jobCard?.install_state };
}
